import React, { useState, useEffect, useCallback } from 'react';
import { MessageSquare, Send, RefreshCw, Phone } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { toast } from 'sonner';

const MessagesPanel = () => {
  const [messages, setMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/messages?limit=100`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setMessages(data);
      } else {
        toast.error('Failed to load messages');
      }
    } catch (error) {
      console.error('Error loading messages:', error);
      toast.error('Error loading messages');
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/messages/unread/count`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.unread_count);
      }
    } catch (error) {
      // Silent fail for unread count - not critical for UX
      console.error('Error fetching unread count:', error);
    }
  }, [API_URL]);

  const sendReply = async () => {
    if (!replyMessage.trim() || !selectedConversation) return;

    setSending(true);
    try {
      const response = await fetch(`${API_URL}/api/messages/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          to_number: selectedConversation,
          message_body: replyMessage
        })
      });

      if (response.ok) {
        toast.success('Message sent!');
        setReplyMessage('');
        fetchMessages();
      } else {
        toast.error('Failed to send message');
      }
    } catch (error) {
      toast.error('Error sending message');
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    fetchMessages();
    fetchUnreadCount();
    
    // Poll for new messages every 30 seconds
    const interval = setInterval(() => {
      fetchMessages();
      fetchUnreadCount();
    }, 30000);

    return () => clearInterval(interval);
  }, [fetchMessages, fetchUnreadCount]);

  // Group messages by phone number
  const groupedMessages = messages.reduce((acc, msg) => {
    const phoneNumber = msg.direction === 'inbound' ? msg.from_number : msg.to_number;
    if (!acc[phoneNumber]) {
      acc[phoneNumber] = [];
    }
    acc[phoneNumber].push(msg);
    return acc;
  }, {});

  const conversations = Object.keys(groupedMessages);

  return (
    <div className="grid md:grid-cols-3 gap-6">
      {/* Conversations List */}
      <Card className="bg-zinc-900 border-zinc-800 md:col-span-1">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <MessageSquare className="w-5 h-5 text-red-500" />
              <CardTitle className="text-white">Conversations</CardTitle>
            </div>
            {unreadCount > 0 && (
              <Badge className="bg-red-600">{unreadCount}</Badge>
            )}
          </div>
          <CardDescription className="text-gray-400">
            SMS messages from customers
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <Button
            onClick={fetchMessages}
            variant="outline"
            className="w-full mb-4"
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>

          {conversations.length === 0 ? (
            <p className="text-gray-400 text-sm text-center py-4">
              No messages yet
            </p>
          ) : (
            conversations.map((phoneNumber) => {
              const msgs = groupedMessages[phoneNumber];
              const latestMsg = msgs[msgs.length - 1];
              const hasUnread = msgs.some(m => !m.read && m.direction === 'inbound');

              return (
                <button
                  key={phoneNumber}
                  onClick={() => setSelectedConversation(phoneNumber)}
                  className={`w-full text-left p-3 rounded-lg transition-colors ${
                    selectedConversation === phoneNumber
                      ? 'bg-red-600'
                      : 'bg-zinc-800 hover:bg-zinc-700'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center space-x-2">
                      <Phone className="w-4 h-4 text-red-500" />
                      <span className="text-white font-semibold text-sm">
                        {phoneNumber}
                      </span>
                    </div>
                    {hasUnread && (
                      <Badge className="bg-red-600 text-xs">New</Badge>
                    )}
                  </div>
                  <p className="text-gray-400 text-xs truncate">
                    {latestMsg.message_body}
                  </p>
                  <p className="text-gray-500 text-xs mt-1">
                    {new Date(latestMsg.created_at).toLocaleString()}
                  </p>
                </button>
              );
            })
          )}
        </CardContent>
      </Card>

      {/* Conversation View */}
      <Card className="bg-zinc-900 border-zinc-800 md:col-span-2">
        <CardHeader>
          <CardTitle className="text-white">
            {selectedConversation || 'Select a conversation'}
          </CardTitle>
          <CardDescription className="text-gray-400">
            Two-way SMS messaging with customers
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!selectedConversation ? (
            <div className="text-center text-gray-400 py-12">
              <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Select a conversation to view messages</p>
            </div>
          ) : (
            <>
              {/* Messages */}
              <div className="space-y-4 mb-6 max-h-96 overflow-y-auto">
                {groupedMessages[selectedConversation]?.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[70%] rounded-lg p-3 ${
                        msg.direction === 'outbound'
                          ? 'bg-red-600 text-white'
                          : 'bg-zinc-800 text-white'
                      }`}
                    >
                      <p className="text-sm">{msg.message_body}</p>
                      <p className={`text-xs mt-1 ${
                        msg.direction === 'outbound' ? 'text-red-100' : 'text-gray-400'
                      }`}>
                        {new Date(msg.created_at).toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Reply Input */}
              <div className="space-y-3">
                <Textarea
                  placeholder="Type your message..."
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  rows={3}
                />
                <Button
                  onClick={sendReply}
                  disabled={!replyMessage.trim() || sending}
                  className="w-full bg-red-600 hover:bg-red-700"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {sending ? 'Sending...' : 'Send Message'}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MessagesPanel;
